import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { isTouch, isBottom, isScrolled, headerHeight } from '../utils';
import * as scrollbar from './scrollbar';

gsap.registerPlugin(ScrollTrigger);
gsap.config({ force3D: true });

export let lenis: Lenis;

export const stop = (): void => {
    lenis.stop();
};

export const start = (): void => {
    lenis.start();
};

// Initialisation du scroll avec Lenis
export const init = (): void => {
    lenis = new Lenis({
        lerp: isTouch() ? 1 : 0.15,
        syncTouch: false,
    });

    lenis.on('scroll', () => {
        ScrollTrigger.update();
        setupScrollTop();
    });

    gsap.ticker.add((time: number) => {
        lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    isBottom(lenis, 50);
    isScrolled(lenis, 6, 0);
    scrollbar.init(lenis);
    // setupDragScroll();
    setupScrollTop();
    setupHideHeader();

    // Recalcul après chargement complet des ressources (images, fonts)
    window.addEventListener('load', () => {
        lenis.resize();
        ScrollTrigger.refresh();
        setTimeout(() => {
            lenis.resize();
            ScrollTrigger.refresh();
            setupHashScroll();
        }, 300);
    });


    // Recalcul automatique si la hauteur de la page change (images lazy, fonts, accordéons...)
    let resizeTimer: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            lenis.resize();
            ScrollTrigger.refresh();
        }, 100);
    });
    observer.observe(document.body);
};

// Drag Scroll
const setupDragScroll = (): void => {
    if (isTouch()) return;

    let isDragging = false;
    let startY = 0;
    let startScroll = 0;
    let lastY = 0;
    let velocity = 0;
    let lastTime = 0;
    let rafId: number | null = null;

    document.addEventListener('mousedown', (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest('a, button, swiper-lazy')) return;
        if (target.closest('.scrollbar-track, .scrollbar-thumb')) return;

        e.preventDefault();

        // Annuler l'inertie en cours si on re-clique
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }

        isDragging = true;
        startY = e.clientY;
        lastY = e.clientY;
        lastTime = performance.now();
        startScroll = lenis.actualScroll;
        velocity = 0;

        /*
        document.body.classList.add('dragging');
        setModifier('grab');
        */
    });

    document.addEventListener('wheel', () => {
        if (!isDragging && rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
            velocity = 0;
        }
    }, { passive: true });

    document.addEventListener('mousemove', (e: MouseEvent) => {
        if (!isDragging) return;

        const now = performance.now();
        const dt = now - lastTime;

        // Vélocité en px/ms
        if (dt > 0) {
            velocity = (lastY - e.clientY) / dt;
        }

        lastY = e.clientY;
        lastTime = now;

        const delta = startY - e.clientY;
        lenis.scrollTo(startScroll + delta, { lerp: 1 });
    });

    document.addEventListener('mouseup', () => {
        if (!isDragging) return;
        isDragging = false;
        /*
        document.body.classList.remove('dragging');
        setModifier(null);
        */

        // Inertie post-release
        const friction = 0.96; // 0.9 = s'arrête vite, 0.98 = glisse longtemps

        const applyMomentum = (): void => {
            if (Math.abs(velocity) < 0.01) return;

            velocity *= friction;
            lenis.scrollTo(lenis.actualScroll + velocity * 18, { lerp: 1 });
            rafId = requestAnimationFrame(applyMomentum);
        };

        rafId = requestAnimationFrame(applyMomentum);
    });
};

// Scroll vers l'ancre
const setupHashScroll = (): void => {
    const hash = window.location.hash;
    if (!hash) return;

    const target = document.querySelector<HTMLElement>(hash);
    if (!target) return;

    lenis.scrollTo(target, { duration: 1, offset: headerHeight() });
};

// Gestion du bouton "Scroll Top"
const setupScrollTop = (): void => {
    const scrollBtn = document.querySelector<HTMLElement>('.scroll-top');

    if (scrollBtn) {
        if (!scrollBtn.dataset.listenerAdded) {
            scrollBtn.addEventListener('click', () => {
                lenis.scrollTo('top', { duration: 0.8 });
            });
            scrollBtn.dataset.listenerAdded = 'true';
        }
    }
};

// Gestion du header hide/show au scroll
const setupHideHeader = (): void => {
    const header = document.querySelector<HTMLElement>('header');
    if (!header) return;

    const threshold = 80;
    const velocityThreshold = 0.05; // ignorer les micro-mouvements

    lenis.on('scroll', ({ scroll, velocity }: { scroll: number; velocity: number }) => {
        if (scroll < 0) return;

        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        if (scroll > maxScroll) return;

        if (scroll < threshold) {
            header.classList.remove('header--hidden');
            return;
        }

        // Ignorer les micro-mouvements (fin d'inertie iOS)
        if (Math.abs(velocity) < velocityThreshold) return;

        if (velocity > 0) {
            header.classList.add('header--hidden');
        } else {
            header.classList.remove('header--hidden');
        }
    });
};
